import { Link } from 'react-router-dom';

import type { ComicCard as ComicCardData } from '@/sources/types';
import { cn } from '@/lib/utils';
import { comicCardRestoreKey, type BackLinkState } from '@/lib/navigationState';

export interface ComicCardProps {
  comic: ComicCardData;
  className?: string;
  backState?: BackLinkState;
}

function formatPageCount(pageCount: number): string {
  return `${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`;
}

function formatRating(rating: number): string {
  return Number.isInteger(rating) ? rating.toFixed(0) : rating.toFixed(2);
}

/**
 * Generic, source-agnostic card. Most cover URLs point directly at source CDNs;
 * sources with hot-link protection may hand back worker-proxied image URLs.
 */
export function ComicCard({ comic, className, backState }: ComicCardProps) {
  const restoreComicKey = comicCardRestoreKey(comic.source, comic.id);
  const nextBackState = backState
    ? { ...backState, restoreComicKey: backState.restoreComicKey ?? restoreComicKey }
    : undefined;

  return (
    <Link
      to={`/source/${comic.source}/comic/${encodeURIComponent(comic.id)}`}
      state={nextBackState ? { back: nextBackState } : undefined}
      data-comic-card-key={restoreComicKey}
      className={cn(
        'group flex flex-col gap-2 rounded-lg border border-border/40 bg-card p-2 transition-colors hover:border-border hover:bg-accent/30',
        className,
      )}
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded bg-muted">
        {comic.coverUrl ? (
          <img
            src={comic.coverUrl}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
            no cover
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="line-clamp-2 text-sm font-medium leading-tight">{comic.title}</h3>
        {(comic.pageCount !== undefined || comic.rating !== undefined) && (
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] leading-tight text-muted-foreground">
            {comic.pageCount !== undefined && <span>{formatPageCount(comic.pageCount)}</span>}
            {comic.rating !== undefined && <span>Rating {formatRating(comic.rating)}</span>}
          </div>
        )}
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>{comic.source}</span>
          {comic.language && <span>{comic.language}</span>}
        </div>
      </div>
    </Link>
  );
}
